import { useState, useEffect } from "react";
import { MonitorDown } from "lucide-react";
import AmmoLogo from "./AmmoLogo";
import { useLanguage } from "../context/LanguageContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";

// Kept here rather than in the shared table: they belong to this band and to
// nothing else.
const LABELS = {
  it: {
    install: "Installa sul desktop",
    title: "Installare AMMO Core",
    intro: "Il browser non ha offerto l'installazione automatica. Si fa a mano in un passaggio:",
    chrome: "Chrome: men\u00f9 \u22ee in alto a destra \u2192 Trasmetti, salva e condividi \u2192 Installa pagina come app",
    edge: "Edge: men\u00f9 \u2026 in alto a destra \u2192 App \u2192 Installa questo sito come app",
    note: "Su Safari e Firefox l'installazione non \u00e8 disponibile.",
  },
  en: {
    install: "Install on desktop",
    title: "Installing AMMO Core",
    intro: "The browser did not offer to install it automatically. It takes one step by hand:",
    chrome: "Chrome: \u22ee menu, top right \u2192 Cast, save and share \u2192 Install page as app",
    edge: "Edge: \u2026 menu, top right \u2192 Apps \u2192 Install this site as an app",
    note: "Safari and Firefox do not support installing.",
  },
};

/**
 * The closing band, and the one place that offers to install Core as an app.
 *
 * The button is shown whenever Core is running in a browser tab, not only when
 * the browser happens to be offering its own prompt. That prompt is a
 * single-use event: dismiss it once and it does not fire again until the page
 * is reloaded, which used to leave somebody who cancelled by accident with no
 * way back to it.
 */
export default function Footer() {
  const { language } = useLanguage();
  const labels = LABELS[language] || LABELS.it;

  const [prompt, setPrompt] = useState(null);
  const [standalone, setStandalone] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    const onAvailable = (e) => {
      e.preventDefault();
      setPrompt(e);
    };
    const onInstalled = () => setPrompt(null);

    window.addEventListener("beforeinstallprompt", onAvailable);
    window.addEventListener("appinstalled", onInstalled);

    const media = window.matchMedia?.("(display-mode: standalone)");
    const sync = () => setStandalone(Boolean(media?.matches));
    sync();
    media?.addEventListener?.("change", sync);

    return () => {
      window.removeEventListener("beforeinstallprompt", onAvailable);
      window.removeEventListener("appinstalled", onInstalled);
      media?.removeEventListener?.("change", sync);
    };
  }, []);

  const handleInstall = async () => {
    if (!prompt) {
      setHelpOpen(true);
      return;
    }
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    setPrompt(null);
    if (outcome === "accepted") setStandalone(true);
  };

  return (
    <>
      <footer
        data-testid="app-footer"
        className="mt-12 border-t border-slate-200 bg-white"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-wrap items-center gap-x-3 gap-y-2">
          <AmmoLogo className="w-6 h-6 shrink-0" />
          <span className="text-sm font-semibold text-slate-800">AMMO Core</span>
          <span className="text-slate-300">&middot;</span>
          <span className="text-sm text-slate-600">AMMO Imaging S.r.l.</span>

          <div className="ml-auto flex items-center gap-3">
            {/* Hidden below the medium breakpoint. It says "on desktop", and
                on a phone the equivalent is Add to Home Screen, which lives in
                the browser's share sheet and cannot be triggered from a page —
                so the button would name the wrong thing and then not do it. */}
            {!standalone && (
              <button
                onClick={handleInstall}
                data-testid="footer-install-btn"
                className="hidden md:inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100 hover:border-indigo-300 transition-colors"
              >
                <MonitorDown className="w-3.5 h-3.5" />
                {labels.install}
              </button>
            )}

            <span className="text-xs text-slate-500 tabular-nums">
              &copy; {new Date().getFullYear()}
            </span>
          </div>
        </div>
      </footer>

      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{labels.title}</DialogTitle>
            <DialogDescription>{labels.intro}</DialogDescription>
          </DialogHeader>
          <ul className="space-y-2.5 text-sm text-slate-700">
            <li className="flex gap-2">
              <span className="text-indigo-500">&bull;</span>
              <span>{labels.chrome}</span>
            </li>
            <li className="flex gap-2">
              <span className="text-indigo-500">&bull;</span>
              <span>{labels.edge}</span>
            </li>
          </ul>
          <p className="text-xs text-slate-400">{labels.note}</p>
        </DialogContent>
      </Dialog>
    </>
  );
}
